import z from 'zod';

import { ImageCodec, PlateImage, UnityImage } from './Image';
import {
    CURRENT_VERSION,
    ElementId,
    EmptyChildren,
    emptyChildren,
    UnityElementId,
    Version,
} from '../commons';
import { ELEMENT_TYPES, MIME_TYPES } from '../constants';

export const UnitySlideshow = z.object({
    type: z.literal(ELEMENT_TYPES.SLIDESHOW),
    elementId: UnityElementId,
    templateElement: z.boolean().optional(),
    mimetype: z.literal(MIME_TYPES.SLIDESHOW),
    contentAttributes: z.object({
        id: z.string(),
    }),
    originalSrc: z.string().nullish(),
    version: Version,
});
export type UnitySlideshow = z.infer<typeof UnitySlideshow>;

export const PlateSlideshow = z.object({
    type: z.literal(ELEMENT_TYPES.SLIDESHOW),
    id: ElementId,
    data: z.object({
        templateElement: z.boolean().optional(),
        mimetype: z.literal(MIME_TYPES.SLIDESHOW),
        embed: z.object({
            id: z.string(),
        }),
        originalSrc: z.string().nullish(),
    }),
    children: EmptyChildren,
});
export type SlideshowElement = z.infer<typeof PlateSlideshow>;

export const SlideshowCodec = z.codec(UnitySlideshow, PlateSlideshow, {
    decode: (value) => ({
        type: value.type,
        id: value.elementId ?? crypto.randomUUID(),
        data: {
            mimetype: value.mimetype,
            embed: { id: value.contentAttributes.id },
            originalSrc: value.originalSrc,
            ...(value.templateElement ? { templateElement: value.templateElement } : {}),
        },
        children: emptyChildren,
    }),
    encode: (value) => ({
        type: value.type,
        elementId: value.id,
        version: CURRENT_VERSION,
        mimetype: value.data.mimetype,
        contentAttributes: { id: value.data.embed.id },
        originalSrc: value.data.originalSrc,
        ...(value.data?.templateElement ? { templateElement: value.data.templateElement } : {}),
    }),
});

export const UnityCsSlideshow = z.object({
    type: z.literal(ELEMENT_TYPES.CS_SLIDESHOW),
    elementId: UnityElementId,
    templateElement: z.boolean().optional(),
    slides: z.array(UnityImage),
    version: Version,
});
export type UnityCsSlideshow = z.infer<typeof UnityCsSlideshow>;

export const PlateCsSlideshow = z.object({
    type: z.literal(ELEMENT_TYPES.CS_SLIDESHOW),
    id: ElementId,
    data: z.object({
        templateElement: z.boolean().optional(),
        slides: z.array(PlateImage),
    }),
    children: EmptyChildren,
});

export type CsSlideshowElement = z.infer<typeof PlateCsSlideshow>;

export const CsSlideshowCodec = z.codec(UnityCsSlideshow, PlateCsSlideshow, {
    decode: (value) => ({
        type: value.type,
        id: value.elementId ?? crypto.randomUUID(),
        data: {
            ...(value.templateElement ? { templateElement: value.templateElement } : {}),
            slides: value.slides.map((slide) => ImageCodec.decode(slide)),
        },
        children: emptyChildren,
    }),
    encode: (value) => ({
        type: value.type,
        elementId: value.id,
        version: CURRENT_VERSION,
        ...(value.data?.templateElement ? { templateElement: value.data.templateElement } : {}),
        slides: (value.data?.slides ?? []).map((slide) => ImageCodec.encode(slide)),
    }),
});
